import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export interface ValidationError {
  field: string;
  cell?: string;       // legacy/optional; the modal renders `message` only (DB-native validation omits it)
  message: string;
  section: 'chart' | 'intro' | 'outro' | 'tease';
  position?: number;   // For chart data, which position (1-10)
  isMissingFile?: boolean; // True if URL exists but file doesn't
}

interface ValidationErrorModalProps {
  open: boolean;
  onClose: () => void;
  errors: ValidationError[];
  onNavigateToSection?: (section: 'chart' | 'intro' | 'outro' | 'tease') => void;
}

const sectionLabels: Record<string, string> = {
  chart: 'Chart Data',
  intro: 'Intro Settings',
  outro: 'Outro Settings',
  tease: 'Tease Settings',
};

const sectionColors: Record<string, 'error' | 'warning' | 'info'> = {
  chart: 'error',
  intro: 'warning',
  outro: 'warning',
  tease: 'info',
};

export const ValidationErrorModal: React.FC<ValidationErrorModalProps> = ({
  open,
  onClose,
  errors,
  onNavigateToSection,
}) => {
  // Group errors by section
  const errorsBySection = errors.reduce((acc, error) => {
    if (!acc[error.section]) {
      acc[error.section] = [];
    }
    acc[error.section].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);

  const sectionOrder = ['chart', 'intro', 'outro', 'tease'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorOutlineIcon color="error" />
        <Typography variant="h6">
          Validation Errors ({errors.length})
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The following fields are missing or have invalid values. Please fix these before exporting.
        </Typography>

        {sectionOrder.map((section) => {
          const sectionErrors = errorsBySection[section];
          if (!sectionErrors || sectionErrors.length === 0) return null;

          return (
            <Accordion key={section} defaultExpanded={section === 'chart'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {onNavigateToSection ? (
                    <Link
                      component="button"
                      variant="body1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToSection(section as 'chart' | 'intro' | 'outro' | 'tease');
                      }}
                      sx={{
                        flexGrow: 1,
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        }
                      }}
                    >
                      {sectionLabels[section]}
                      <OpenInNewIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                    </Link>
                  ) : (
                    <Typography sx={{ flexGrow: 1 }}>
                      {sectionLabels[section]}
                    </Typography>
                  )}
                  <Chip
                    label={`${sectionErrors.length} error${sectionErrors.length > 1 ? 's' : ''}`}
                    size="small"
                    color={sectionColors[section]}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense>
                  {sectionErrors.map((error, index) => (
                    <ListItem key={index} divider={index < sectionErrors.length - 1}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {error.isMissingFile ? (
                          <WarningAmberIcon color="warning" fontSize="small" />
                        ) : (
                          <ErrorOutlineIcon color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText primary={error.message} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidationErrorModal;
